import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  FileText, Plus, Edit, Trash2, Eye, Check, X, Users, Clock,
  FileSignature, Printer, Search, RefreshCw, Shield, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { customerPoliciesAPI } from '../../services/api';

// Sanitize HTML helper
const sanitizeHtml = (html) => DOMPurify.sanitize(html);

const CATEGORY_OPTIONS = [
  { value: 'workers_rights', label: "Workers' Rights" },
  { value: 'service_terms', label: 'Service Terms' },
  { value: 'safety', label: 'Safety & Compliance' },
  { value: 'general', label: 'General' },
];

const CustomerPoliciesSection = () => {
  const [policies, setPolicies] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSignaturesModal, setShowSignaturesModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [form, setForm] = useState({
    title: '',
    content: '',
    category: 'general',
    is_required: true,
    requires_signature: true,
    display_order: 0,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [policiesRes, signaturesRes] = await Promise.all([
        customerPoliciesAPI.getAll({ active_only: false }),
        customerPoliciesAPI.getSignatures()
      ]);
      setPolicies(policiesRes.data.policies || []);
      setSignatures(signaturesRes.data.signatures || []);
      setStats(signaturesRes.data.stats);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load customer policies');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const res = await customerPoliciesAPI.seedDefaults();
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed defaults');
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setSubmitting(true);
    try {
      await customerPoliciesAPI.create(form);
      toast.success('Policy created');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content');
      return;
    }
    
    setSubmitting(true);
    try {
      await customerPoliciesAPI.update(selectedPolicy.id, form);
      toast.success('Policy updated');
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (policy) => {
    if (!window.confirm(`Delete "${policy.title}"? This cannot be undone.`)) return;
    
    try {
      await customerPoliciesAPI.delete(policy.id);
      toast.success('Policy deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handleToggleActive = async (policy) => {
    try {
      await customerPoliciesAPI.update(policy.id, { is_active: !policy.is_active });
      toast.success(policy.is_active ? 'Policy deactivated' : 'Policy activated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update policy');
    }
  };

  const openEditModal = (policy) => {
    setForm({
      title: policy.title,
      content: policy.content,
      category: policy.category,
      is_required: policy.is_required,
      requires_signature: policy.requires_signature,
      display_order: policy.display_order,
    });
    setSelectedPolicy(policy);
    setShowEditModal(true);
  };

  const viewSignatures = async (policy) => {
    setSelectedPolicy(policy);
    try {
      const res = await customerPoliciesAPI.getSignatures(policy.id);
      setSignatures(res.data.signatures || []);
      setShowSignaturesModal(true);
    } catch (error) {
      toast.error('Failed to load signatures');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      content: '',
      category: 'general',
      is_required: true,
      requires_signature: true,
      display_order: 0,
    });
  };

  const handlePrintPolicy = (policy) => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${policy.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
          h1 { color: #1a365d; border-bottom: 2px solid #1a365d; padding-bottom: 10px; }
          .meta { color: #666; font-size: 12px; margin-bottom: 20px; }
          .content { white-space: pre-wrap; }
          .signature-line { margin-top: 60px; border-top: 1px solid #000; width: 300px; padding-top: 5px; }
          .footer { margin-top: 40px; font-size: 11px; color: #999; }
        </style>
      </head>
      <body>
        <h1>${policy.title}</h1>
        <div class="meta">
          Category: ${CATEGORY_OPTIONS.find(c => c.value === policy.category)?.label || policy.category} | 
          ${policy.is_required ? 'Required' : 'Optional'} | 
          ${policy.requires_signature ? 'Signature Required' : 'No Signature Required'}
        </div>
        <div class="content">${policy.content}</div>
        ${policy.requires_signature ? `
        <div class="signature-line">
          <p>Customer Signature</p>
          <p style="font-size: 11px; color: #666;">Date: _________________</p>
        </div>
        ` : ''}
        <div class="footer">
          <p>NC Help Hub Customer Policy Document</p>
          <p>Printed: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      const doc = printWindow.document;
      const html = doc.createElement('html');
      const head = doc.createElement('head');
      const body = doc.createElement('body');
      const container = doc.createElement('div');
      container.innerHTML = sanitizeHtml(printContent);
      body.appendChild(container);
      html.appendChild(head);
      html.appendChild(body);
      doc.documentElement.replaceWith(html);
      setTimeout(() => { printWindow.focus(); printWindow.print(); }, 300);
    }
  };

  const filteredPolicies = policies.filter(p => 
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.content?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSignatureCount = (policyId) => {
    return signatures.filter(s => s.policy_id === policyId).length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin w-8 h-8 border-4 border-[var(--primary)] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Customer Policies</h2>
          <p className="text-sm text-[var(--text-muted)]">Policies customers must review and sign</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          {policies.length === 0 && (
            <Button onClick={handleSeedDefaults} variant="outline" size="sm">
              <Shield className="w-4 h-4 mr-1" />
              Add Default Policies
            </Button>
          )}
          <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="btn-primary" size="sm">
            <Plus className="w-4 h-4 mr-1" />
            Add Policy
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--primary)]">{stats.total_customers || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Total Customers</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-[var(--success)]">{stats.customers_with_signatures || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Signed Policies</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-2xl font-bold text-amber-600">{stats.customers_without_signatures || 0}</p>
            <p className="text-sm text-[var(--text-muted)]">Pending Signatures</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search policies..."
          className="pl-10"
        />
      </div>

      {/* Policies List */}
      {filteredPolicies.length === 0 ? (
        <div className="card p-12 text-center">
          <FileText className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="font-semibold mb-2">No Customer Policies</h3>
          <p className="text-[var(--text-muted)] mb-4">Create policies for customers to review and sign</p>
          <Button onClick={handleSeedDefaults} variant="outline">
            <Shield className="w-4 h-4 mr-2" />
            Add Default Policies
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredPolicies.map(policy => (
            <div 
              key={policy.id} 
              className={`card p-4 ${!policy.is_active ? 'opacity-60' : ''}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-semibold">{policy.title}</h3>
                    {!policy.is_active && (
                      <span className="text-xs px-2 py-0.5 rounded bg-[var(--background-subtle)] text-[var(--text-secondary)]">Inactive</span>
                    )}
                    {policy.is_required && (
                      <span className="text-xs px-2 py-0.5 rounded bg-[var(--error)]/15 text-red-700">Required</span>
                    )}
                  </div>
                  <p className="text-sm text-[var(--text-muted)]">
                    {CATEGORY_OPTIONS.find(c => c.value === policy.category)?.label || policy.category}
                  </p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => viewSignatures(policy)} title="View Signatures">
                    <Users className="w-4 h-4" />
                    <span className="ml-1 text-xs">{getSignatureCount(policy.id)}</span>
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handlePrintPolicy(policy)} title="Print">
                    <Printer className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEditModal(policy)} title="Edit">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleToggleActive(policy)}
                    title={policy.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {policy.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(policy)} title="Delete">
                    <Trash2 className="w-4 h-4 text-[var(--error)]" />
                  </Button>
                </div>
              </div>
              <p className="text-sm text-[var(--text-secondary)] line-clamp-3">{policy.content}</p>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b sticky top-0 bg-[var(--background-paper)]">
              <h2 className="text-xl font-bold">Create Customer Policy</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  placeholder="e.g., Workers' Rights Acknowledgment"
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({...form, display_order: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Content *</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm({...form, content: e.target.value})}
                  placeholder="Enter the full policy text..."
                  className="mt-1 min-h-[200px] font-mono text-sm"
                />
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.is_required}
                    onCheckedChange={(v) => setForm({...form, is_required: v})}
                  />
                  <span className="text-sm">Required (customers must sign)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.requires_signature}
                    onCheckedChange={(v) => setForm({...form, requires_signature: v})}
                  />
                  <span className="text-sm">Requires signature</span>
                </label>
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3 justify-end sticky bottom-0 bg-[var(--background-paper)]">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={submitting} className="btn-primary">
                {submitting ? 'Creating...' : 'Create Policy'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b sticky top-0 bg-[var(--background-paper)]">
              <h2 className="text-xl font-bold">Edit Customer Policy</h2>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  className="mt-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Display Order</Label>
                  <Input
                    type="number"
                    value={form.display_order}
                    onChange={(e) => setForm({...form, display_order: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label>Content *</Label>
                <Textarea
                  value={form.content}
                  onChange={(e) => setForm({...form, content: e.target.value})}
                  className="mt-1 min-h-[200px] font-mono text-sm"
                />
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.is_required}
                    onCheckedChange={(v) => setForm({...form, is_required: v})}
                  />
                  <span className="text-sm">Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.requires_signature}
                    onCheckedChange={(v) => setForm({...form, requires_signature: v})}
                  />
                  <span className="text-sm">Requires signature</span>
                </label>
              </div>
            </div>
            
            <div className="p-6 border-t flex gap-3 justify-end sticky bottom-0 bg-[var(--background-paper)]">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={submitting} className="btn-primary">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Signatures Modal */}
      {showSignaturesModal && selectedPolicy && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--background-paper)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b sticky top-0 bg-[var(--background-paper)]">
              <h2 className="text-xl font-bold">Signatures</h2>
              <p className="text-sm text-[var(--text-muted)]">{selectedPolicy.title}</p>
            </div>
            
            <div className="p-6">
              {signatures.length === 0 ? (
                <div className="text-center py-8">
                  <FileSignature className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
                  <p className="text-[var(--text-muted)]">No signatures yet</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {signatures.map(sig => (
                    <div key={sig.id} className="p-3 bg-[var(--background-subtle)] rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{sig.customer_name}</p>
                          <p className="text-sm text-[var(--text-muted)]">{sig.customer_email}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-[var(--text-muted)]">Signed</p>
                          <p className="text-sm">{new Date(sig.signed_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="p-6 border-t">
              <Button variant="outline" onClick={() => setShowSignaturesModal(false)} className="w-full">
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CustomerPoliciesSection;
